import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Languages, Check, ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const languageOptions = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();
  const { waLink } = usePublicContent();
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { hash: "why-choose-us", label: t.nav.why },
    { to: "/contact", label: t.nav.contact },
  ];

  const LanguageSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-2 rounded-full border border-[#12284B]/20 bg-white px-4 py-2 text-sm font-head font-semibold text-[#12284B] hover:border-[#E84B1C] transition-colors focus:outline-none">
          <Languages size={17} className="text-[#E84B1C]" />
          Language
          <ChevronDown size={15} className="text-[#12284B]/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border-[#12284B]/15 max-h-80 overflow-y-auto w-44">
        {languageOptions.map((o) => (
          <DropdownMenuItem
            key={o.code}
            onClick={() => setLang(o.code)}
            className="flex items-center justify-between font-head text-[#12284B] cursor-pointer focus:bg-[#E84B1C] focus:text-white"
          >
            {o.label}
            {lang === o.code && <Check size={16} className="text-[#E84B1C]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg shadow-[#12284B]/10"
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0">
          <img
            src="/superhost-logo.png"
            alt="Superhost Cloud"
            className="h-10 sm:h-12 lg:h-14 w-auto"
          />
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {navItems.map((item) =>
            item.hash ? (
              <button
                key={item.hash}
                onClick={() => goToSection(item.hash)}
                data-testid={`nav-${item.hash}`}
                className="font-head text-[15px] font-medium tracking-wide text-[#12284B] hover:text-[#E84B1C] transition-colors"
              >
                {item.label}
              </button>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `font-head text-[15px] font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-[#E84B1C]"
                      : "text-[#12284B] hover:text-[#E84B1C]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSelector />
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageSelector />
          <button
            className="text-[#12284B] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-white ${
          open ? "max-h-96 border-t border-[#12284B]/10 shadow-lg" : "max-h-0"
        }`}
      >
        <div className="px-5 py-4 flex flex-col gap-1">
          {navItems.map((item) =>
            item.hash ? (
              <button
                key={item.hash}
                onClick={() => goToSection(item.hash)}
                data-testid={`nav-mobile-${item.hash}`}
                className="font-head py-3 px-2 rounded-lg text-base font-medium text-left text-[#12284B]"
              >
                {item.label}
              </button>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-head py-3 px-2 rounded-lg text-base font-medium ${
                    isActive ? "text-[#E84B1C]" : "text-[#12284B]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#E84B1C] text-white font-head font-semibold px-5 py-3 rounded-full"
          >
            <MessageCircle size={18} /> {t.nav.whatsapp}
          </a>
        </div>
      </div>
    </header>
  );
}
